import { Cpu, Eye, FileSearch, Lock, ShieldCheck, Type } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { PRIVACY_STATEMENT, TESSDATA_SOURCE } from '../../lib/ocr/constants';

const STEPS = [
  {
    icon: FileSearch,
    title: 'Add and adjust',
    body: 'Drop, browse or paste your images and PDFs. Every file is checked by its real header bytes, then you can rotate or crop before recognition.',
  },
  {
    icon: Cpu,
    title: 'Recognise in your browser',
    body: 'Tesseract LSTM (and optionally PaddleOCR PP-OCRv5 for English) runs on WebAssembly inside this tab. Files are processed one at a time.',
  },
  {
    icon: Type,
    title: 'Edit and export',
    body: 'Review the text, fix anything you want, then copy a single result, copy the whole batch, or download a plain .txt file.',
  },
];

const CAPABILITIES = [
  ['English', 'Classic OCR and Next-Gen OCR. Screenshots and clean scans are the strongest case.'],
  ['Hindi (Devanagari)', 'Classic OCR with the full-accuracy hin model from tessdata_best.'],
  ['Sanskrit (Devanagari)', 'Classic OCR with the full-accuracy san model. Verse layouts and conjuncts are harder.'],
  [
    'Hindi + Sanskrit (Devanagari)',
    'Loads hin + san together and deliberately leaves English out, so Latin guesses cannot compete with Devanagari.',
  ],
  ['Mixed English + Hindi + Sanskrit', 'Loads eng + hin + san for pages that genuinely switch script.'],
  [
    'Tables and invoices',
    'Word positions are kept after recognition, then rows and columns are rebuilt from geometry so fields stay associated.',
  ],
];

const LIMITS = [
  'Accuracy is never 100%. Always read the result before you rely on it.',
  'Handwriting, heavy blur, decorative display fonts and physically damaged documents are experimental and often wrong.',
  'Text that is genuinely tiny in the original file cannot be recovered by upscaling — we tell you when that happens instead of pretending it is fixed.',
  'Very low-resolution text, tight line spacing and coloured text over photographs reduce accuracy.',
  'Sanskrit conjuncts and stacked matras are the hardest case in Devanagari recognition.',
  'Table reconstruction works from geometry. Borderless layouts with irregular spacing may still merge or split columns.',
  'HEIC, TIFF and GIF are not supported because they are not decoded and tested here.',
  'Next-Gen OCR is English-only in this app. Hindi, Sanskrit and mixed pages always use Classic OCR.',
];

const FAQ = [
  {
    q: 'Are my images uploaded to a server?',
    a: 'No. Recognition runs inside your browser using WebAssembly. Your images and the extracted text stay on your device and are never uploaded or stored by this app.',
  },
  {
    q: 'Which languages can Text Extractor read?',
    a: 'Classic OCR reads English, Hindi (Devanagari), Sanskrit (Devanagari) and mixed English/Hindi/Sanskrit pages. Next-Gen OCR is English-only in this app.',
  },
  {
    q: 'How accurate is the text extraction?',
    a: 'Clean printed text usually extracts very well. Accuracy is never 100%. Handwriting, heavy blur, decorative fonts and damaged documents are experimental and often imperfect, so always review the result.',
  },
  {
    q: 'Does it work fully offline?',
    a: 'Not completely. Language data is served from this site, but the recognition worker, the WebAssembly core and the Next-Gen models are fetched from pinned public CDNs on first use. After that your browser cache handles repeat runs. Only model assets are downloaded — never your files.',
  },
  {
    q: 'Can it read multi-page scanned PDFs?',
    a: 'Yes. PDFs up to 20 MB and 20 pages per run are rendered page by page in your browser. Pages that already contain selectable text are read directly; the rest go through OCR.',
  },
  {
    q: 'Why is a poster giving worse results than a document?',
    a: 'Posters use scattered layouts, several colours and decorative type. Switch the recognition mode to Poster / Mixed Layout, and crop closer to the text block if the result is still weak.',
  },
];

export const HowItWorks = () => (
  <section aria-labelledby="how-heading" className="scroll-mt-24" id="how-it-works">
    <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="how-heading">
      How it works
    </h2>
    <div className="mt-5 grid gap-4 md:grid-cols-3">
      {STEPS.map((step, index) => (
        <div className="surface-card p-5" key={step.title}>
          <div className="flex items-center gap-3">
            <span className="brand-gradient flex h-9 w-9 items-center justify-center rounded-xl text-sm font-bold text-white">
              {index + 1}
            </span>
            <step.icon aria-hidden="true" className="h-4.5 w-4.5 text-indigo-600" />
          </div>
          <h3 className="mt-4 font-display text-base font-bold text-slate-900">{step.title}</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{step.body}</p>
        </div>
      ))}
    </div>
  </section>
);

export const LanguagesAndLimits = () => (
  <section aria-labelledby="languages-heading" className="scroll-mt-24" id="languages">
    <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="languages-heading">
      Supported languages and honest limitations
    </h2>
    <div className="mt-5 grid gap-4 lg:grid-cols-2">
      <div className="surface-card p-5">
        <h3 className="font-display text-base font-bold text-slate-900">What is supported</h3>
        <dl className="mt-4 space-y-4">
          {CAPABILITIES.map(([name, detail]) => (
            <div key={name}>
              <dt className="text-sm font-semibold text-slate-900">{name}</dt>
              <dd className="mt-1 text-sm leading-relaxed text-slate-600">{detail}</dd>
            </div>
          ))}
        </dl>
      </div>
      <div className="surface-card p-5">
        <h3 className="font-display text-base font-bold text-slate-900">Where it struggles</h3>
        <ul className="mt-4 space-y-3">
          {LIMITS.map((limit) => (
            <li className="flex items-start gap-2.5 text-sm leading-relaxed text-slate-600" key={limit}>
              <Eye aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              {limit}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

export const PrivacySection = () => (
  <section aria-labelledby="privacy-heading" className="scroll-mt-24" id="privacy">
    <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="privacy-heading">
      Privacy
    </h2>
    <div className="surface-card mt-5 p-5 sm:p-6">
      <p className="flex items-start gap-3 text-base font-semibold text-slate-900">
        <ShieldCheck aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
        {PRIVACY_STATEMENT}
      </p>
      <ul className="mt-5 grid gap-3 text-sm leading-relaxed text-slate-600 md:grid-cols-2">
        <li className="flex items-start gap-2.5">
          <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          There is no account, no database and no server-side image handling. Nothing is persisted after you
          close or refresh the tab.
        </li>
        <li className="flex items-start gap-2.5">
          <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          No large language model is called, and no external OCR API is contacted. Recognition happens on your
          CPU through WebAssembly.
        </li>
        <li className="flex items-start gap-2.5">
          <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          Extracted text is never logged to the console, sent to analytics or attached to error reports.
          Analytics session recording is switched off on this page.
        </li>
        <li className="flex items-start gap-2.5">
          <Lock aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-indigo-600" />
          Preview object URLs are revoked and temporary canvases are released as soon as a file leaves the
          queue.
        </li>
      </ul>
      <div className="mt-5 rounded-xl bg-[#f5f6fd] px-4 py-3 text-xs leading-relaxed text-slate-600">
        <p className="font-semibold text-slate-800">What is downloaded (never uploaded)</p>
        <p className="mt-1">
          Trained language data: {TESSDATA_SOURCE}. The Tesseract worker script and WebAssembly core come from
          version-pinned <code>cdn.jsdelivr.net</code> URLs, and Next-Gen OCR additionally fetches the ONNX
          Runtime Web build from jsDelivr plus the PP-OCRv5 models from the official PaddlePaddle model host.
          These are model and runtime assets only — this app cannot work fully offline, and we do not claim that
          it does.
        </p>
      </div>
    </div>
  </section>
);

export const FaqSection = () => (
  <section aria-labelledby="faq-heading" className="scroll-mt-24" id="faq">
    <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" id="faq-heading">
      Frequently asked questions
    </h2>
    <div className="surface-card mt-5 px-5 py-2 sm:px-6">
      <Accordion collapsible type="single">
        {FAQ.map((entry, index) => (
          <AccordionItem key={entry.q} value={`faq-${index}`}>
            <AccordionTrigger className="text-left text-sm font-semibold text-slate-900" data-testid={`faq-trigger-${index}`}>
              {entry.q}
            </AccordionTrigger>
            <AccordionContent className="text-sm leading-relaxed text-slate-600">{entry.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
